<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import MdiCloudUpload from '~icons/mdi/cloud-upload'
import MdiFileExcel from '~icons/mdi/file-excel'
import MdiFileCsv from '~icons/mdi/file-delimited'
import MdiClose from '~icons/mdi/close'
import { createImportSession, getImportSession } from '@/api/api.js'
import BaseInput from '@/components/base/BaseInput.vue'

const emit = defineEmits(['session-created'])
const props = defineProps({ session: { type: Object, default: null } })
const { t } = useI18n()

const file = ref(null)
const fileInput = ref(null)
const dragOver = ref(false)
const loading = ref(false)
const error = ref('')
const pollingInterval = ref(null)

onMounted(() => {
  if (props.session && ['uploading', 'parsing'].includes(props.session.status)) {
    loading.value = true
    pollUntilParsed(props.session.id)
  } else if (props.session?.status === 'failed') {
    error.value = props.session.error_message || t('import.parseFailedMsg')
  }
})
onUnmounted(() => clearInterval(pollingInterval.value))

const fileDisplay = computed(() => {
  if (!file.value) return null
  const ext = file.value.name.split('.').pop().toLowerCase()
  return {
    name: file.value.name,
    size: (file.value.size / 1024).toFixed(1) + ' KB',
    isExcel: ext === 'xlsx'
  }
})

function onDragOver(e) {
  e.preventDefault()
  dragOver.value = true
}

function onDragLeave() {
  dragOver.value = false
}

function onDrop(e) {
  e.preventDefault()
  dragOver.value = false
  const dropped = e.dataTransfer.files[0]
  if (dropped) setFile(dropped)
}

function onFileInput(e) {
  const picked = e.target.files[0]
  if (picked) setFile(picked)
}

function setFile(f) {
  error.value = ''
  file.value = null
  const ext = f.name.split('.').pop().toLowerCase()
  if (!['csv', 'xlsx'].includes(ext)) {
    error.value = t('import.invalidFormat')
    return
  }
  if (f.size > 50 * 1024 * 1024) {
    error.value = t('import.fileTooLarge')
    return
  }
  file.value = f
}

function clearFile() {
  file.value = null
  error.value = ''
}

async function startUpload() {
  if (!file.value) return
  loading.value = true
  error.value = ''

  try {
    const formData = new FormData()
    formData.append('file', file.value)
    const response = await createImportSession(formData)
    const session = response
    pollUntilParsed(session.id)
  } catch (err) {
    error.value = err?.data?.error || t('import.uploadFailed')
    loading.value = false
  }
}

function pollUntilParsed(sessionId) {
  error.value = ''
  loading.value = true
  if (pollingInterval.value) clearInterval(pollingInterval.value)
  pollingInterval.value = setInterval(async () => {
    try {
      const response = await getImportSession(sessionId)
      const session = response
      if (session.status === 'parsed') {
        clearInterval(pollingInterval.value)
        loading.value = false
        emit('session-created', session)
      } else if (session.status === 'failed') {
        clearInterval(pollingInterval.value)
        loading.value = false
        error.value =
          session.error_message || t('import.parseFailedMsg')
      }
    } catch {
      clearInterval(pollingInterval.value)
      loading.value = false
      error.value = t('import.statusCheckError')
    }
  }, 2000)
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <p class="text-(--color-muted) text-sm">
        {{ $t('import.uploadInstructions') }}
      </p>
    </div>

    <!-- Drop zone -->
    <div
      class="relative flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-12 text-center transition-colors cursor-pointer"
      :class="
        dragOver
          ? 'border-(--color-accent) bg-(--color-accent)/10'
          : 'border-(--color-border) hover:border-(--color-accent)/50'
      "
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
      @click="fileInput?.click()"
    >
      <BaseInput ref="fileInput" type="file" accept=".csv,.xlsx" class="hidden" @change="onFileInput" />

      <template v-if="!fileDisplay">
        <MdiCloudUpload class="w-12 h-12 text-(--color-muted)" />
        <div>
          <p class="text-(--color-text) font-medium">{{ $t('import.dropYourFile') }}</p>
          <p class="text-(--color-muted) text-sm mt-1">{{ $t('import.supportedFormats') }}</p>
        </div>
      </template>

      <template v-else>
        <component
          :is="fileDisplay.isExcel ? MdiFileExcel : MdiFileCsv"
          class="w-12 h-12 text-(--color-accent)"
        />
        <div>
          <p class="text-(--color-text) font-medium">{{ fileDisplay.name }}</p>
          <p class="text-(--color-muted) text-sm">{{ fileDisplay.size }}</p>
        </div>
        <button class="btn btn-ghost btn-small absolute top-3 right-3" @click.stop="clearFile">
          <MdiClose class="w-4 h-4" />
        </button>
      </template>
    </div>

    <!-- Parsing indicator -->
    <div v-if="loading" class="flex items-center gap-3 text-(--color-muted) text-sm">
      <span
        class="inline-block w-4 h-4 rounded-full border-2 border-(--color-accent) border-t-transparent animate-spin"
      />
      {{ $t('import.parsingFile') }}
    </div>

    <!-- Error -->
    <div v-if="error" class="flex items-center gap-2 text-red-400 text-sm" role="alert">
      <p>{{ error }}</p>
      <button v-if="session && ['uploading', 'parsing'].includes(session.status) && !loading" type="button" class="btn btn-ghost btn-small" @click="pollUntilParsed(session.id)">
        {{ $t('import.tryAgain') }}
      </button>
    </div>

    <!-- CTA -->
    <div class="flex justify-end">
      <button class="btn btn-primary" :disabled="!file || loading" @click="startUpload">
        {{ loading ? $t('import.uploading') : $t('import.uploadAndParse') }}
      </button>
    </div>
  </div>
</template>
